// Central LLM client. In v2.0 every AI operation routes through the
// lens-api /api/ai/anthropic proxy. The Anthropic API key lives server-side
// in Key Vault and never reaches the browser. Provider is Claude only —
// Gemini and Ollama were retired with the migration.
import { api } from "../api/_client";

export type LlmContentBlock =
  | { type: "text"; text: string }
  | { type: "image"; base64: string; mimeType: "image/png" | "image/jpeg" }
  | { type: "document"; text: string }; // plain text from PDF extraction

export interface LlmRequest {
  systemPrompt: string;
  userContent: LlmContentBlock[];
  schema: object;
  maxTokens?: number;
  temperature?: number;
  model?: string;
}

// Kept for backwards-compat with callers that switched providers at runtime.
export type LlmConfig = {
  id: string;
  provider: "anthropic";
  model: string;
};

export class LlmError extends Error {
  constructor(
    public code: "schema_mismatch" | "quota_exceeded" | "network" | "auth" | "no_vision" | "unknown",
    message: string,
  ) {
    super(message);
    this.name = "LlmError";
  }
}

// Anthropic /v1/messages response shape (minimal fields we care about)
interface AnthropicResponse {
  content?: Array<{ type: string; text?: string }>;
  stop_reason?: string;
  usage?: { input_tokens: number; output_tokens: number };
}

/**
 * Execute an AI request. Server-side proxy holds credentials; response is
 * validated to be a JSON object matching the caller's schema (schema check
 * currently happens on the caller side via JSON.parse — full schema
 * enforcement is a Session 5+ concern once v2.1's evaluation harness lands).
 */
export async function llmRequest<T>(req: LlmRequest): Promise<T> {
  let response: AnthropicResponse;
  try {
    response = await api<AnthropicResponse>("POST", "/api/ai/anthropic", req);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (message.includes("429") || message.toLowerCase().includes("quota")) {
      throw new LlmError("quota_exceeded", message);
    }
    if (message.includes("401") || message.includes("403") || message.toLowerCase().includes("unauthor")) {
      throw new LlmError("auth", message);
    }
    throw new LlmError("network", message);
  }

  const text = response.content?.[0]?.text;
  if (!text) throw new LlmError("unknown", "Empty response from Anthropic");

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new LlmError("schema_mismatch", `Response not valid JSON: ${text.substring(0, 200)}`);
  }
}
